import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Alert,
  Snackbar,
  Divider,
  Chip,
  useMediaQuery,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import api from '../../api';
import RequestItemBuilder, { itemsToPayload, templateItemsToFormItems } from './RequestItemBuilder';
import StatusPill from './StatusPill';
import { createClientRequest, sendClientRequest, listClientRequestTemplates } from '../../api/clientRequests';

const STEPS = ['Basics', 'Build items', 'Reminders', 'Preview & send'];

const reminderPresetEnum = z.enum(['none', 'three_days', 'weekly', 'custom']);

const formSchema = z.object({
  client_id: z.union([z.number(), z.null()]),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  due_date: z.string().optional(),
  assigned_to_id: z.union([z.number(), z.null()]).optional(),
  items: z.array(z.object({ label: z.string().optional() }).passthrough()).optional(),
  reminder_preset: reminderPresetEnum,
  custom_when: z.string().optional(),
  custom_frequency: z.enum(['daily', 'weekly']).optional(),
  custom_until: z.string().optional(),
});

function buildReminderSchedule(values) {
  const { reminder_preset: preset, due_date: due, custom_when: cw, custom_frequency: cf, custom_until: cu } = values;
  if (preset === 'none') return { preset: 'none' };
  if (preset === 'three_days') return { preset: 'three_days_before_due', anchor: due || null };
  if (preset === 'weekly') return { preset: 'weekly_until_complete', recurring: 'weekly_until_complete' };
  return {
    preset: 'custom',
    start_at: cw || null,
    frequency: cf || 'weekly',
    until: cu || 'complete',
  };
}

export default function CreateRequestDialog({ open, onClose, initialClientId, onCreated }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      client_id: initialClientId || null,
      title: '',
      description: '',
      due_date: '',
      assigned_to_id: null,
      items: [],
      reminder_preset: 'weekly',
      custom_when: '',
      custom_frequency: 'weekly',
      custom_until: 'complete',
    },
  });

  const { control, reset, watch, setValue, formState: { errors } } = methods;
  const items = watch('items') || [];
  const clientId = watch('client_id');
  const reminderPreset = watch('reminder_preset');

  useEffect(() => {
    if (!open) {
      setStep(0);
      reset({
        client_id: initialClientId || null,
        title: '',
        description: '',
        due_date: '',
        assigned_to_id: null,
        items: [],
        reminder_preset: 'weekly',
        custom_when: '',
        custom_frequency: 'weekly',
        custom_until: 'complete',
      });
      return;
    }
    setLoadingClients(true);
    const token = localStorage.getItem('access_token');
    axios
      .get('/api/clients/', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setClients(res.data?.results || res.data || []))
      .catch(() => setClients([]))
      .finally(() => setLoadingClients(false));

    api
      .get('/api/multi-tenant/users/')
      .then((res) => {
        const data = res.data?.results ?? res.data;
        setUsers(Array.isArray(data) ? data : []);
      })
      .catch(() => setUsers([]));

    setTemplatesLoading(true);
    listClientRequestTemplates()
      .then((res) => setTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [open, initialClientId, reset]);

  const clientLocked = Boolean(initialClientId);

  const validateStep = () => {
    if (step === 0) {
      if (!clientId) {
        setSnack({ severity: 'error', message: 'Select a client.' });
        return false;
      }
      const t = watch('title');
      if (!t || !String(t).trim()) {
        setSnack({ severity: 'error', message: 'Enter a title.' });
        return false;
      }
      return true;
    }
    if (step === 1) {
      if (!items.length) {
        setSnack({ severity: 'error', message: 'Add at least one item.' });
        return false;
      }
      const bad = items.some((i) => !(i.label || '').trim());
      if (bad) {
        setSnack({ severity: 'error', message: 'Each item needs a label.' });
        return false;
      }
      return true;
    }
    if (step === 2 && reminderPreset === 'custom') {
      const cw = watch('custom_when');
      if (!cw) {
        setSnack({ severity: 'error', message: 'Choose when to send the first custom reminder.' });
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const applyTemplate = (tpl) => {
    if (!tpl?.items?.length) return;
    setValue('items', templateItemsToFormItems(tpl.items));
    setSnack({ severity: 'info', message: `Loaded template: ${tpl.name}` });
  };

  const clientEntity = useMemo(
    () => clients.find((c) => c.id === clientId),
    [clients, clientId]
  );
  const clientName = clientEntity
    ? clientEntity.display_name || `${clientEntity.first_name || ''} ${clientEntity.last_name || ''}`.trim()
    : '';

  const persist = async (andSend) => {
    const vals = methods.getValues();
    const schedule = buildReminderSchedule(vals);
    const payload = {
      client_id: vals.client_id,
      title: vals.title.trim(),
      description: vals.description || '',
      request_type: 'documents',
      due_date: vals.due_date || null,
      assigned_to_id: vals.assigned_to_id || null,
      reminder_schedule: schedule,
      items: itemsToPayload(vals.items || []),
    };

    setSaving(true);
    try {
      const created = await createClientRequest(payload).then((r) => r.data);
      if (andSend) {
        await sendClientRequest(created.id);
        setSnack({ severity: 'success', message: `Request sent to ${clientName || 'client'}` });
      } else {
        setSnack({ severity: 'success', message: 'Saved as draft.' });
      }
      if (onCreated) onCreated(created);
      setTimeout(onClose, 900);
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Could not save request.';
      setSnack({ severity: 'error', message: typeof msg === 'string' ? msg : 'Could not save request.' });
    } finally {
      setSaving(false);
    }
  };

  const previewItems = watch('items') || [];

  return (
    <FormProvider {...methods}>
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{ sx: { minHeight: fullScreen ? '100%' : '60vh' } }}
        >
          <DialogTitle>
            <Typography fontWeight={700}>New Client Request</Typography>
            <Stepper activeStep={step} sx={{ mt: 1.5 }} alternativeLabel>
              {STEPS.map((s) => (
                <Step key={s}>
                  <StepLabel>
                    <Typography variant="caption">{s}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </DialogTitle>

          <DialogContent dividers sx={{ py: 3 }}>
            {step === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Controller
                  name="client_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disabled={clientLocked}
                      options={clients}
                      getOptionLabel={(o) => o.display_name || `${o.first_name || ''} ${o.last_name || ''}`.trim()}
                      value={clients.find((c) => c.id === field.value) || null}
                      onChange={(_, val) => field.onChange(val?.id ?? null)}
                      loading={loadingClients}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          required
                          size="small"
                          error={Boolean(errors.client_id)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingClients ? <CircularProgress size={14} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                    />
                  )}
                />
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Title"
                      required
                      fullWidth
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} size="small" label="Description (optional)" multiline minRows={2} fullWidth />
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Controller
                    name="due_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        label="Due date"
                        type="date"
                        sx={{ flex: 1, minWidth: 160 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="assigned_to_id"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        sx={{ flex: 1, minWidth: 200 }}
                        options={users}
                        getOptionLabel={(u) =>
                          u.first_name || u.username ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username : u.email || ''
                        }
                        value={users.find((u) => u.id === field.value) || null}
                        onChange={(_, val) => field.onChange(val?.id ?? null)}
                        renderInput={(params) => <TextField {...params} label="Assigned to" size="small" />}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                      />
                    )}
                  />
                </Box>
              </Box>
            )}

            {step === 1 && (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Use template
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 220 }} disabled={templatesLoading || !templates.length}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      label="Template"
                      value=""
                      displayEmpty
                      onChange={(e) => {
                        const id = e.target.value;
                        const tpl = templates.find((t) => String(t.id) === String(id));
                        if (tpl) applyTemplate(tpl);
                      }}
                    >
                      <MenuItem value="">
                        <em>{templatesLoading ? 'Loading…' : 'Choose a template…'}</em>
                      </MenuItem>
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <RequestItemBuilder
                  items={items}
                  onChange={(next) => setValue('items', next)}
                  clientId={clientId}
                />
              </Box>
            )}

            {step === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Choose how reminders are sent before the due date and until the request is completed.
                </Typography>
                <Controller
                  name="reminder_preset"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <RadioGroup {...field}>
                        <FormControlLabel value="none" control={<Radio size="small" />} label="None" />
                        <FormControlLabel
                          value="three_days"
                          control={<Radio size="small" />}
                          label="3 days before due date"
                        />
                        <FormControlLabel
                          value="weekly"
                          control={<Radio size="small" />}
                          label="Weekly until complete"
                        />
                        <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom schedule" />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
                {reminderPreset === 'custom' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
                    <Controller
                      name="custom_when"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          type="datetime-local"
                          label="First reminder"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="custom_frequency"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small">
                          <InputLabel>Frequency</InputLabel>
                          <Select label="Frequency" {...field}>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="custom_until"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small">
                          <InputLabel>Until</InputLabel>
                          <Select label="Until" {...field}>
                            <MenuItem value="complete">Request is complete</MenuItem>
                            <MenuItem value="due_date">Due date</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>
                )}
              </Box>
            )}

            {step === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography fontWeight={700}>{watch('title')}</Typography>
                  <StatusPill status="draft" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Client: <strong>{clientName || '—'}</strong>
                </Typography>
                {watch('description') && (
                  <Typography variant="body2">{watch('description')}</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {watch('due_date') && (
                    <Chip
                      label={`Due ${new Date(watch('due_date')).toLocaleDateString()}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  {previewItems.length} item{previewItems.length !== 1 ? 's' : ''}
                </Typography>
                {previewItems.map((item, i) => (
                  <Box key={item._key || i} sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {(item.label || '').trim() || <em>Untitled</em>}
                    </Typography>
                    {!item.is_required && <Chip label="Optional" size="small" />}
                  </Box>
                ))}
                {previewItems.length === 0 && <Alert severity="warning">Add at least one item before sending.</Alert>}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} disabled={saving}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {step < STEPS.length - 1 ? (
                <Button variant="contained" onClick={goNext}>
                  Next
                </Button>
              ) : (
                <>
                  <Button onClick={() => persist(false)} disabled={saving || !previewItems.length}>
                    {saving ? <CircularProgress size={14} /> : 'Save as Draft'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => persist(true)}
                    disabled={saving || !previewItems.length}
                  >
                    {saving ? <CircularProgress size={14} color="inherit" /> : 'Send to Client'}
                  </Button>
                </>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {snack && (
            <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
              {snack.message}
            </Alert>
          )}
        </Snackbar>
      </>
    </FormProvider>
  );
}
